import { Injectable, computed, signal } from '@angular/core';
import { HdBodygraphActivation, HdBodygraphPlanetaryActivation } from '../../bodygraph/hd-bodygraph.models';
import {
  HD_LOCKS_KEYS_DEFAULT_PERIOD_ID,
  HD_LOCKS_KEYS_DEFAULT_PERIOD_PAIR,
  HD_LOCKS_KEYS_PERIODS,
  HdLocksKeysChartOverlayMode,
  HdLocksKeysPeriodId,
  HdLocksKeysScope,
  HdLocksKeysSearchFilter,
  HdLocksKeysSearchSort,
  hdLocksKeysPeriodForDate,
  hdLocksKeysPeriodIndex,
  isHdLocksKeysPeriodId,
} from './hd-locks-keys-metadata';
import { buildHdLocksKeysOverview } from './hd-locks-keys';

/**
 * Gedeelde weergavestaat van de Locks & Keys-modus. De chart-pagina bezit de
 * modus zelf (aan/uit, wederzijdse uitsluiting met de transit-modi) en spiegelt
 * de chart-activaties hierheen; het gedockte infopaneel en het detailpaneel
 * lezen en schrijven scope/periode/vergelijk rechtstreeks — hetzelfde
 * service-patroon als `HdTransitsViewStateService`, zodat het floating-panel
 * geen live callbacks door zijn data-token hoeft te slepen.
 */
@Injectable({ providedIn: 'root' })
export class HdLocksKeysStateService {
  /** Neutrale ster of toegepast op de geladen chart. */
  readonly scope = signal<HdLocksKeysScope>('chart');

  /**
   * Geselecteerde keys-periode (globaal persistent via de chart-settings).
   * Altijd een van de twee periodes uit {@link periodPair}: schrijf via de
   * methodes hieronder, niet rechtstreeks.
   */
  readonly periodId = signal<HdLocksKeysPeriodId>(HD_LOCKS_KEYS_DEFAULT_PERIOD_ID);

  /**
   * De twee periodes van de tweeluik-toggle. Bij het laden van de settings en
   * bij het openen van de modus staat het paar op Planning en Sleeping Phoenix
   * (nu en na 2027, twee opeenvolgende tijdperken). Enkel de dropdowns zetten
   * één kant vrij op een ander tijdperk; de pijltjes, een gekozen tabelrij of
   * vlechtkolom en de poort-info schuiven beide kanten samen, met behoud van
   * hun afstand.
   */
  readonly periodPair = signal<readonly [HdLocksKeysPeriodId, HdLocksKeysPeriodId]>(HD_LOCKS_KEYS_DEFAULT_PERIOD_PAIR);

  /** Actieve kant van het paar (0 = links, 1 = rechts). */
  readonly activeSlot = computed<0 | 1>(() => (this.periodPair()[1] === this.periodId() ? 1 : 0));

  /** Kunnen de pijltjes het paar nog een tijdperk terug (-1) of verder (+1) schuiven? */
  readonly canShiftEarlier = computed(() => this.canShiftPair(-1));
  readonly canShiftLater = computed(() => this.canShiftPair(1));

  /** Vergelijkweergave: mandala per periode naast elkaar met info ertussen. */
  readonly compare = signal(false);

  /** 8-puntige locks-ster (twee vierkanten over de lock-wiggen) tonen. */
  readonly showLocksStar = signal(false);

  /** 8-puntige keys-ster (de twee incarnatiekruisen van de periode) tonen. */
  readonly showKeysStar = signal(false);

  /**
   * Locks & keys op de bodygraph buiten de modus (drie-standen-toggle in het
   * Geavanceerd-paneel). Een inhoudelijke weergavevoorkeur: bewaard via de
   * chart-settings, net als {@link periodId}.
   */
  readonly chartOverlayMode = signal<HdLocksKeysChartOverlayMode>('off');

  /**
   * Weergavekeuzes van de L&K-zoekresultaten (sortering en kruisfilter): bewaard
   * via de chart-settings, net als {@link chartOverlayMode}.
   */
  readonly searchSort = signal<HdLocksKeysSearchSort>('relations');
  readonly searchFilter = signal<HdLocksKeysSearchFilter>('all');

  /**
   * Opent de volledige Locks & Keys-modus, optioneel in een gegeven periode.
   * Enkel de chart-pagina registreert dit (en wist het bij vernietiging, zoals
   * `printHandler` in de settings-store); `null` = we zitten niet op een
   * chart-pagina en de modus kan niet geactiveerd worden (bv. cheat sheets).
   */
  readonly modeLauncher = signal<((periodId?: HdLocksKeysPeriodId) => void) | null>(null);

  /** Door de pagina gespiegeld: activaties van de getoonde chart. */
  readonly chartActivations = signal<readonly HdBodygraphActivation[]>([]);

  /** Door de pagina gespiegeld: planetaire activaties (planeetchips in het paneel). */
  readonly chartPlanetaryActivations = signal<readonly HdBodygraphPlanetaryActivation[]>([]);

  /**
   * Tweede kant op een relatie-/composite-tab: de volledige chart van de
   * partner (chart 2). Leeg buiten composite-tabs. Keys tellen als "samen
   * ingevuld" wanneer één van beide charts de key-poort activeert.
   */
  readonly partnerActivations = signal<readonly HdBodygraphActivation[]>([]);
  readonly partnerPlanetaryActivations = signal<readonly HdBodygraphPlanetaryActivation[]>([]);

  /**
   * Namen van de twee kanten op een composite/relatie-tab (door de page
   * gespiegeld). Leeg buiten composite; de relatie-sectie valt dan terug op de
   * generieke "Chart 1"/"Chart 2"-labels.
   */
  readonly chart1Name = signal('');
  readonly chart2Name = signal('');

  /** Composite-modus: het paneel kleurt chips per persoon i.p.v. per zijde. */
  readonly hasPartner = computed(() => this.partnerActivations().length > 0);

  /** Unie van beide kanten — de bron voor alle key-activatie-checks. */
  readonly combinedActivations = computed<readonly HdBodygraphActivation[]>(() =>
    this.partnerActivations().length === 0
      ? this.chartActivations()
      : [...this.chartActivations(), ...this.partnerActivations()],
  );

  readonly hasChart = computed(() => this.combinedActivations().length > 0);

  /** Effectieve scope: zonder chart valt "op chart" terug op de neutrale weergave. */
  readonly effectiveScope = computed<HdLocksKeysScope>(() =>
    this.scope() === 'chart' && this.hasChart() ? 'chart' : 'general',
  );

  /** Overzicht van de geselecteerde periode in de effectieve scope. */
  readonly overview = computed(() =>
    buildHdLocksKeysOverview(this.periodId(), this.effectiveScope(), this.combinedActivations()),
  );

  /** Overzicht per willekeurige periode (voor de vergelijkweergave). */
  overviewFor(periodId: HdLocksKeysPeriodId) {
    return buildHdLocksKeysOverview(periodId, this.effectiveScope(), this.combinedActivations());
  }

  /** Maakt een kant van het paar actief. */
  selectSlot(slot: 0 | 1): void {
    this.periodId.set(this.periodPair()[slot]);
  }

  /**
   * Zet één kant op een andere periode (mini-dropdown) en maakt die kant
   * actief; de andere kant blijft staan. Dit is de enige weg naar twee
   * uiteenliggende tijdperken. Kiest men de periode van de andere kant, dan
   * wordt gewoon die kant actief: beide kanten op hetzelfde tijdperk
   * vergelijkt niets.
   */
  setSlotPeriod(slot: 0 | 1, periodId: HdLocksKeysPeriodId): void {
    if (!isHdLocksKeysPeriodId(periodId)) return;
    const pair = this.periodPair();
    if (pair[1 - slot] === periodId) {
      this.periodId.set(periodId);
      return;
    }
    const next: [HdLocksKeysPeriodId, HdLocksKeysPeriodId] = [pair[0], pair[1]];
    next[slot] = periodId;
    this.periodPair.set(next);
    this.periodId.set(periodId);
  }

  /**
   * Kiest een periode van buitenaf (Global Cycles-rij of -vlecht, poort-info,
   * de modus-launcher): zit ze in het paar, dan wordt die kant actief; anders
   * schuift het paar zodat ze in de actieve kant staat en de andere kant op
   * dezelfde afstand meebeweegt.
   */
  selectPeriod(periodId: HdLocksKeysPeriodId): void {
    if (!isHdLocksKeysPeriodId(periodId)) return;
    const slot = this.periodPair().indexOf(periodId);
    if (slot === 0 || slot === 1) {
      this.selectSlot(slot);
      return;
    }
    this.placeInPair(periodId, this.activeSlot());
  }

  /** Pijltjes: schuift beide kanten samen één tijdperk op, met behoud van afstand en actieve kant. */
  shiftPair(direction: -1 | 1): void {
    if (!this.canShiftPair(direction)) return;
    const slot = this.activeSlot();
    const [first, second] = this.periodPair().map(id => HD_LOCKS_KEYS_PERIODS[hdLocksKeysPeriodIndex(id) + direction].id);
    this.periodPair.set([first, second]);
    this.periodId.set(slot === 0 ? first : second);
  }

  /**
   * Zet het paar terug naar het begin van de tabel (het vroegste tijdperk van
   * het paar wordt 16513 v.Chr.), met behoud van afstand en actieve kant. De
   * afspeelknop begint zo opnieuw wanneer het paar al achteraan staat.
   */
  rewindPair(): void {
    const indexes = this.periodPair().map(hdLocksKeysPeriodIndex);
    const offset = Math.min(...indexes);
    if (offset <= 0) return;
    const slot = this.activeSlot();
    const [first, second] = indexes.map(index => HD_LOCKS_KEYS_PERIODS[index - offset].id);
    this.periodPair.set([first, second]);
    this.periodId.set(slot === 0 ? first : second);
  }

  /**
   * Herstelt de bewaarde periode bij het laden van de settings: het paar start
   * altijd op dat van nu (Planning en Sleeping Phoenix), met de bewaarde kant
   * actief; een bewaard tijdperk daarbuiten wordt het tijdperk van vandaag.
   */
  restorePeriods(savedPeriodId: unknown): void {
    this.resetToDefaultPair(savedPeriodId);
  }

  /** Herstelt de bewaarde zoekweergave; onbekende waarden vallen terug op de standaard. */
  restoreSearchPreferences(sort: unknown, filter: unknown): void {
    this.searchSort.set(sort === 'name' ? 'name' : 'relations');
    this.searchFilter.set(filter === 'cross' ? 'cross' : 'all');
  }

  /**
   * Zet `periodId` in kant `slot` en laat de andere kant op dezelfde afstand
   * meebewegen (twee opeenvolgende tijdperken blijven opeenvolgend), en maakt
   * die periode actief. Valt de andere kant buiten de tabel, dan komt de
   * periode aan de andere kant; past ook dat niet, dan klemt de andere kant
   * tegen de rand van de tabel.
   */
  private placeInPair(periodId: HdLocksKeysPeriodId, slot: 0 | 1): void {
    const [left, right] = this.periodPair().map(hdLocksKeysPeriodIndex);
    const offset = right - left;
    const index = hdLocksKeysPeriodIndex(periodId);
    const lastIndex = HD_LOCKS_KEYS_PERIODS.length - 1;
    const fits = (pair: readonly number[]) => pair.every(i => i >= 0 && i <= lastIndex);
    const clamp = (i: number) => Math.min(Math.max(i, 0), lastIndex);
    const preferred = slot === 0 ? [index, index + offset] : [index - offset, index];
    const flipped = slot === 0 ? [index - offset, index] : [index, index + offset];
    const [nextLeft, nextRight] = fits(preferred)
      ? preferred
      : fits(flipped)
        ? flipped
        : preferred.map(clamp);
    this.periodPair.set([HD_LOCKS_KEYS_PERIODS[nextLeft].id, HD_LOCKS_KEYS_PERIODS[nextRight].id]);
    this.periodId.set(periodId);
  }

  /**
   * Het standaardpaar (nu en na 2027) met `preferred` actief als die erin zit,
   * anders het tijdperk van vandaag.
   */
  private resetToDefaultPair(preferred: unknown): void {
    const pair = HD_LOCKS_KEYS_DEFAULT_PERIOD_PAIR;
    this.periodPair.set(pair);
    this.periodId.set(typeof preferred === 'string' && pair.includes(preferred) ? preferred : hdLocksKeysPeriodForDate());
  }

  private canShiftPair(direction: -1 | 1): boolean {
    return this.periodPair().every(id => {
      const target = hdLocksKeysPeriodIndex(id) + direction;
      return target >= 0 && target < HD_LOCKS_KEYS_PERIODS.length;
    });
  }

  /**
   * Startpunt bij het activeren van de modus: op chart wanneer die er is, en
   * op het periodepaar van nu met de actieve kant behouden als die erin zit.
   * Beide sterren starten uit (bijstuurronde 2) — de legende-checkboxes
   * onderaan het paneel zetten ze aan.
   */
  resetForActivation(hasChart: boolean): void {
    this.resetToDefaultPair(this.periodId());
    this.scope.set(hasChart ? 'chart' : 'general');
    this.compare.set(false);
    this.showLocksStar.set(false);
    this.showKeysStar.set(false);
  }
}
